import { useEffect, useRef, useCallback, useState } from 'react';
import { GazeData } from '../types';
import { EMA_ALPHA } from '../config/constants';
import { headVectorToYaw, headVectorToPitch } from '../engine/stats';
import {
  AdaptiveGazeEnhancer,
  DEFAULT_GAZE_ENHANCEMENT_PARAMS,
  DEFAULT_GAZE_ENHANCEMENT_TOGGLES,
} from '../engine/gazeEnhancement';
import WebcamClient from '../lib/webeyetrack/WebcamClient';
import WebEyeTrackProxy from '../lib/webeyetrack/WebEyeTrackProxy';
import type { GazeResult } from '../lib/webeyetrack/types';

/**
 * WebEyeTracker integration hook with AdaptiveGazeEnhancer.
 * Uses locally copied WebcamClient + WebEyeTrackProxy (no npm package dependency).
 */
export function useWebEyeTracker(videoElementId: string) {
  const [gazeData, setGazeData] = useState<GazeData | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const proxyRef = useRef<WebEyeTrackProxy | null>(null);
  const smoothYawRef = useRef(0);
  const smoothPitchRef = useRef(0);
  const lastUpdateRef = useRef(0);
  const isReadyRef = useRef(false);

  const enhancerRef = useRef(new AdaptiveGazeEnhancer(
    DEFAULT_GAZE_ENHANCEMENT_PARAMS,
    {
      ...DEFAULT_GAZE_ENHANCEMENT_TOGGLES,
      enableKalman: true,
      enableLagCompensation: true,
      enableAxisAsymmetry: true,
    },
  ));

  useEffect(() => {
    // NOTE: no "already initialized" ref guard here. Under React 18 StrictMode
    // the effect runs → cleanup → runs again; a ref guard combined with the
    // clearTimeout below would cancel the first scheduled init() and then block
    // the second from ever re-scheduling → init() never runs (silent "loading…",
    // no console output). Each run gets its own `disposed` closure + cleanup
    // dispose, so a cancelled run proceeds no further and only one proxy lives.
    let disposed = false;

    async function init() {
      try {
        // Wait for video element
        let videoEl: HTMLVideoElement | null = null;
        for (let i = 0; i < 20; i++) {
          videoEl = document.getElementById(videoElementId) as HTMLVideoElement;
          if (videoEl) break;
          await new Promise(r => setTimeout(r, 200));
        }
        if (!videoEl || disposed) {
          setError('Video element not found');
          return;
        }

        console.log('[useWebEyeTracker] Creating WebcamClient...');
        const webcamClient = new WebcamClient(videoElementId);

        console.log('[useWebEyeTracker] Creating WebEyeTrackProxy...');
        const proxy = new WebEyeTrackProxy(webcamClient, {
          // Worker served under /static in BOTH environments:
          //  - dev: public/static/514.index.js  → /static/514.index.js (real file, no SPA fallback)
          //  - prod (proctoringdemo): app/static/514.index.js → /static/514.index.js (FastAPI /static mount)
          // The worker fetches its model relative to its own URL, so it resolves at
          // /static/web/model.json — present in dev (public/static/web/) and prod (app/static/web/).
          // This single path works whether the app is served at root (dev) or under
          // a sub-path (/proctoring on the server), which '/514.index.js' did not.
          workerFactory: () => new Worker('/static/514.index.js', { type: 'classic' }),
          autoAttachClickListener: false,
          trackerOptions: {
            smoothing: { type: 'none' as any },
            output: {
              includeEyePatch: true,
              includeFaceLandmarks: true,
              includeBlendshapes: false,
            },
          },
        });

        if (disposed) {
          (proxy as any).dispose?.();
          return;
        }

        proxyRef.current = proxy;
        enhancerRef.current.reset(0, 0, Date.now());

        proxy.onGazeResults = (result: GazeResult) => {
          if (disposed || !result) return;

          const now = Date.now();
          if (now - lastUpdateRef.current < 40) return;
          lastUpdateRef.current = now;

          const rawNormX = result.normPog?.[0] ?? 0;
          const rawNormY = result.normPog?.[1] ?? 0;
          const gazeOpen = (result as any).gazeState === 'open';

          // AdaptiveGazeEnhancer (Kalman + lag compensation)
          const enhanced = enhancerRef.current.update({
            rawNormX,
            rawNormY,
            timestampMs: result.timestamp ?? now,
            gazeOpen,
          });

          const finalPog = enhanced.compensatedNorm;

          // Head pose
          const hv = result.headVector || [0, 0, -1];
          const rawYaw = headVectorToYaw(hv as number[]);
          const rawPitch = -headVectorToPitch(hv as number[]); // 부호 반전: 위를 보면 양수
          smoothYawRef.current += EMA_ALPHA * (rawYaw - smoothYawRef.current);
          smoothPitchRef.current += EMA_ALPHA * (rawPitch - smoothPitchRef.current);

          setGazeData({
            normPog: [finalPog[0], finalPog[1]] as [number, number],
            headVector: [hv[0], hv[1], hv[2]] as [number, number, number],
            gazeState: gazeOpen ? 'open' : 'closed',
            timestamp: result.timestamp ?? now,
            yaw: smoothYawRef.current,
            pitch: smoothPitchRef.current,
          });

          if (!isReadyRef.current) {
            isReadyRef.current = true;
            setIsReady(true);
            console.log('[useWebEyeTracker] ✅ BlazeGaze active — first GazeResult received');
          }
        };

        // Error handlers
        (proxy as any).onWorkerError = (msg: string) => {
          console.error('[useWebEyeTracker] Worker error:', msg);
          setError(msg);
        };
        (proxy as any).onWebcamError = (err: unknown) => {
          console.error('[useWebEyeTracker] Webcam error:', err);
          setError(String(err));
        };

        console.log('[useWebEyeTracker] Proxy created, waiting for BlazeGaze model...');
      } catch (err: any) {
        console.error('[useWebEyeTracker] Init failed:', err);
        setError(err?.message || 'Init failed');
      }
    }

    const timer = setTimeout(init, 500);

    return () => {
      disposed = true;
      clearTimeout(timer);
      (proxyRef.current as any)?.dispose?.();
    };
  }, [videoElementId]);

  const startCalibration = useCallback(() => {
    (proxyRef.current as any)?.startCalibration?.();
  }, []);

  const endCalibration = useCallback(() => {
    (proxyRef.current as any)?.endCalibration?.();
  }, []);

  const handleCalibClick = useCallback((normX: number, normY: number) => {
    (proxyRef.current as any)?.handleClick?.(normX, normY);
  }, []);

  return { gazeData, isReady, error, startCalibration, endCalibration, handleCalibClick };
}
